import { Dialog, Transition } from '@headlessui/react';
import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaDownload, FaTrash, FaXmark } from 'react-icons/fa6';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { Photo } from '../../types/photoAlbum';
import Avatar from '../misc/Avatar';
import Button from '../misc/Button';

interface PhotoViewerModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  photo: Photo | null;
  photos?: Photo[];
  onDelete?: (photoId: number) => void;
  onNavigate?: (photo: Photo) => void;
}

export default function PhotoViewerModal({
  open,
  setOpen,
  photo,
  photos = [],
  onDelete,
  onNavigate,
}: PhotoViewerModalProps): JSX.Element {
  const { t } = useTranslation();
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!photo) {
    return <></>;
  }

  const currentIndex = photos.findIndex((p) => p.id === photo.id);
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < photos.length - 1;

  const handlePrevious = () => {
    if (hasPrevious && onNavigate) {
      onNavigate(photos[currentIndex - 1]);
    }
  };

  const handleNext = () => {
    if (hasNext && onNavigate) {
      onNavigate(photos[currentIndex + 1]);
    }
  };

  const handleDownload = async () => {
    if (!photo?.pictureUrl) return;

    try {
      const response = await fetch(photo.pictureUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const extension = photo.pictureUrl.split('.').pop()?.toLowerCase() || 'jpg';
      link.download = `photo-${photo.id}-${new Date(photo.createdAt).toISOString().split('T')[0]}.${extension}`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('toast.error.loadFailed'));
    }
  };

  const handleDeleteClick = () => {
    if (confirmDelete) {
      onDelete?.(photo.id);
      setConfirmDelete(false);
      setOpen(false);
    } else {
      setConfirmDelete(true);
    }
  };

  const handleClose = () => {
    setConfirmDelete(false);
    setOpen(false);
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        {/* Backdrop */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/80 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="relative w-full max-w-4xl transform overflow-hidden rounded-2xl bg-secondary-50 shadow-2xl transition-all">
                {/* Close button */}
                <button
                  type="button"
                  onClick={handleClose}
                  className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white transition-colors hover:bg-black/70">
                  <FaXmark className="h-5 w-5" />
                </button>

                {/* Navigation arrows */}
                {photos.length > 1 && (
                  <>
                    {hasPrevious && (
                      <button
                        type="button"
                        onClick={handlePrevious}
                        className="absolute left-4 top-[35%] z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70">
                        <FaChevronLeft className="h-5 w-5" />
                      </button>
                    )}
                    {hasNext && (
                      <button
                        type="button"
                        onClick={handleNext}
                        className="absolute right-4 top-[35%] z-10 -translate-y-1/2 rounded-full bg-black/50 p-3 text-white transition-colors hover:bg-black/70">
                        <FaChevronRight className="h-5 w-5" />
                      </button>
                    )}
                  </>
                )}

                {/* Image */}
                <div className="relative flex h-[60vh] items-center justify-center bg-black">
                  <img
                    src={photo.pictureUrl}
                    alt={`${new Date(photo.createdAt).toLocaleDateString()}-${photo.uploader.firstName}-${photo.uploader.lastName}`}
                    className="h-full w-full object-contain"
                  />
                </div>

                {/* Info section */}
                <div className="p-6">
                  {/* Uploader info */}
                  <div className="mb-4 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <Avatar
                        src={undefined}
                        alias={`${photo.uploader.firstName} ${photo.uploader.lastName}`}
                        className="h-10 w-10"
                      />
                      <div>
                        <p className="font-medium text-primary">
                          {photo.uploader.firstName} {photo.uploader.lastName}
                        </p>
                        <p className="text-sm text-details/70">
                          {new Date(photo.createdAt).toLocaleDateString(undefined, {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex items-center justify-between">
                    {onDelete && (
                      <Button variant="primary" onClick={handleDeleteClick} className="bg-red-500 hover:bg-red-600">
                        <FaTrash className="h-4 w-4" />
                        {confirmDelete ? t('general.confirmDelete') : t('general.delete')}
                      </Button>
                    )}

                    <Button variant="primary" onClick={handleDownload}>
                      <FaDownload className="h-4 w-4" />
                      {t('general.download')}
                    </Button>
                  </div>

                  {/* Photo counter */}
                  {photos.length > 1 && (
                    <p className="mt-4 text-center text-sm text-details/60">
                      {currentIndex + 1} / {photos.length}
                    </p>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
